package com.advaita.cloud.drive.ui.store

import kotlin.math.floor

/** The text of an Expires cell. [soon] is true for the badge (14 days or less, v59 `expiry`). */
data class ExpiryLabel(val text: String, val soon: Boolean)

const val EXPIRY_SOON_DAYS = 14
private const val DAY_MS = 86_400_000.0

/**
 * The Expires cell for a share. A share with no end reads "Never". A share that ends in 14 days or
 * less carries [ExpiryLabel.soon] and reads "in N days", where N counts whole days and never rounds
 * up. Under one day reads "today". A later end, and an end that has passed, read as the date. [nowMs]
 * is the clock in milliseconds, so a test fixes it. Example: at noon on 2 Oct, an end at 14:00
 * that day reads "today", an end at 11:00 on 4 Oct reads "in 1 day", and an end on 1 Oct reads
 * "2026-10-01".
 */
fun expiryLabel(expiresAtIso: String?, nowMs: Double): ExpiryLabel {
    if (expiresAtIso.isNullOrBlank()) return ExpiryLabel("Never", false)
    val at = kotlin.js.Date(expiresAtIso).getTime()
    if (at.isNaN()) return ExpiryLabel(expiresAtIso.take(10), false)
    if (at <= nowMs) return ExpiryLabel(expiresAtIso.take(10), false)
    val days = floor((at - nowMs) / DAY_MS).toInt()
    return when {
        days == 0 -> ExpiryLabel("today", true)
        days <= EXPIRY_SOON_DAYS -> ExpiryLabel(if (days == 1) "in 1 day" else "in $days days", true)
        else -> ExpiryLabel(expiresAtIso.take(10), false)
    }
}

/** True when the share has an end that [nowMs] has reached. No end, or an unreadable one, is not expired. */
fun isExpired(expiresAtIso: String?, nowMs: Double): Boolean {
    if (expiresAtIso.isNullOrBlank()) return false
    val at = kotlin.js.Date(expiresAtIso).getTime()
    return !at.isNaN() && at <= nowMs
}
